import { useState, useEffect } from 'react';
import { ErrorBoundary } from 'shared-ui';

import { Group } from 'api/groups/groups.types';
import { fetchGroups } from 'api/groups/groups';
import { QueryKeyEnum } from 'core/global.enum';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useSearchQuery } from 'hooks/useSearchQuery/useSearchQuery';
import { GroupsQueryKeysEnum } from 'context/query/queryContext/QueryContext.enum';
import { useSelectedGroup } from 'hooks/useSelectedGroup/useSelectedGroup';
import { useGroupsArchivedView } from 'hooks/useGroupsArchivedView/useGroupsArchivedView';
import { useLocaleStoreGroupsQuery } from 'hooks/useLocaleStoreGroupsQuery/useLocaleStoreGroupsQuery';
import { useSavedPagination } from 'hooks/useSavedPagination/useSavedPagination';

import { Groups } from './Groups';

export const GroupsContainer = () => {
  const [checkedGroups, setCheckedGroups] = useState<Group[]>([]);
  const [groupsTableSearchQuery, setGroupsTableSearchQuery] = useState('');
  const { setSelectedGroupId, setSelectedGroup } = useSelectedGroup();
  const searchQuery = useSearchQuery();
  const redirectedGroupId = searchQuery.get(GroupsQueryKeysEnum.QUERY);

  const { groupsQueryLocalStorageName } = useLocaleStoreGroupsQuery();
  const { isGroupsArchivedView } = useGroupsArchivedView();

  const { page, rowsPerPage, setPage, setRowsPerPage, storedQueryWithoutPagination, setPaginationCount } =
    useSavedPagination({
      queryKey: groupsQueryLocalStorageName,
      resetPaginationDependencyArray: [groupsTableSearchQuery],
    });

  const { data: groupsResponse, isLoading: isLoadingGroups } = useQuery(
    [
      QueryKeyEnum.GROUPS,
      storedQueryWithoutPagination,
      page,
      rowsPerPage,
      groupsTableSearchQuery,
      redirectedGroupId,
      isGroupsArchivedView,
    ],
    fetchGroups,
    {
      args: {
        ...storedQueryWithoutPagination,
        [GroupsQueryKeysEnum.LIMIT]: rowsPerPage,
        [GroupsQueryKeysEnum.OFFSET]: rowsPerPage * page,
        [GroupsQueryKeysEnum.ARCHIWALNA]: isGroupsArchivedView,
        id: redirectedGroupId ? redirectedGroupId : groupsTableSearchQuery,
      },
      onSuccess: (response) => {
        setPaginationCount(response.count);
      },
    },
  );

  useEffect(() => {
    if (redirectedGroupId) {
      setSelectedGroupId(redirectedGroupId);

      const redirectedGroup = groupsResponse?.data.find((group) => group.id === redirectedGroupId);

      if (redirectedGroup) setSelectedGroup(redirectedGroup);
    }
  }, [redirectedGroupId, groupsResponse]);

  useEffect(() => {
    if (!groupsResponse?.data) return;

    setCheckedGroups(
      groupsResponse?.data.filter((group) => checkedGroups.some((checkedGroup) => checkedGroup.id === group.id)),
    );
  }, [groupsResponse]);

  return (
    <ErrorBoundary>
      <Groups
        groupsResponse={groupsResponse}
        isLoadingGroups={isLoadingGroups}
        checkedGroups={checkedGroups}
        setCheckedGroups={setCheckedGroups}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        setGroupsTableSearchQuery={setGroupsTableSearchQuery}
        withBackButton={!!redirectedGroupId}
      />
    </ErrorBoundary>
  );
};
